// ErrorHandlerInterceptor
// Interceptor global de errores HTTP.
// Responsabilidad: mostrar alertas amigables al usuario según el endpoint y el status code.
// Caso especial: /rest/refresh-token con 401 → logout directo sin mostrar alert,
// porque significa que el refresh token fue rechazado explícitamente por el backend
// y no hay forma de recuperarse sin un nuevo login.
//
// Fallo de red (status 0). Si la request llevaba reintentoCtx, el ReintentoInterceptor
// ya agotó los intentos. Qué se hace depende del contexto de la request:
//  - /rest/refresh-token     → silencio (lleva reintentoCtx(false), así que ya se reintentó).
//                              AuthService.refreshToken() lo detecta, navega a /sin-conexion y lanza
//                              SinConexionError: la sesión NO se cierra por un corte de red.
//  - sinConexionCtx()        → navega a /sin-conexion (consultas públicas o indispensables). Se evalúa
//                              antes que REINTENTO porque sinConexionCtx también lo lleva.
//  - reintentoCtx(true)      → genérico + regreso al menú inicial.
//  - reintentoCtx(false)     → silencio y SIN marcar: lo maneja quien hizo la request.
//  - crearCtx(...)           → aviso de alta + botón LISTAR.
//  - editarCtx(...)          → aviso de edición + botón VER (con los signals del ver).
//  - eliminarCtx(...)        → aviso de baja + botón LISTAR.
//  - sin contexto            → genérico sin navegación (red de seguridad).
// Cuando se muestra un cartel, el error se marca como manejado y se relanza (no EMPTY), para
// que los services despachen el error al store sin mostrar un segundo cartel.
// El orden de las ramas importa: si una request llevara reintento y escritura a la vez,
// gana el reintento. Las escrituras nunca deben llevar reintentoCtx.
//
// Errores con respuesta del servidor (status distinto de 0): manejo genérico con handleHttpError,
// salvo crearCtx({ erroresEnComponente: true }) (factura y NC) cuando el body trae internalStatusCS:
// ahí el cartel lo arma el componente y el interceptor solo relanza el error.

import { HttpInterceptorFn, HttpErrorResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, EMPTY, throwError } from "rxjs";
import { AlertService } from "../services/alert.service";
import { ErrorReportingService } from "../services/error-reporting.service";
import { AuthService } from "../services/auth.service";
import { handleHttpError } from "../utils/handle-http-error";
import { ErrorRedService } from "../services/error-red.service";
import { REINTENTO, SIN_CONEXION } from "./reintento-context";
import { CREAR, EDITAR, ELIMINAR } from "./operacion-context";
import { esErrorDeRed, marcarErrorManejado } from "../utils/errores/errores-manejados";

export const ErrorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const alertService   = inject(AlertService);
  const errorReporting = inject(ErrorReportingService);
  const authService    = inject(AuthService);
  const errorRed       = inject(ErrorRedService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Se lee acá: después de la rama de fallo de red, el type guard esErrorDeRed deja `error` como never.
      const codigoInterno = error.error?.internalStatusCS;

      // ── Caso especial: error en el endpoint de refresh-token ──────────────
      if (req.url.includes('/rest/refresh-token')) {

        if (error.status === 401) {
          // El backend rechazó explícitamente el refresh token (inválido o expirado).
          // No hay recuperación posible sin un nuevo login → logout inmediato y silencioso.
          // El Guard se encargará de redirigir al login.
          authService.logout();
          return EMPTY;
        }

        // Fallo de red en el refresh (después del reintento): silencio. AuthService muestra
        // /sin-conexion y avisa a quien llamó que no desloguee (SinConexionError).
        if (esErrorDeRed(error)) {
          marcarErrorManejado(error);
          return throwError(() => error);
        }

        // Otros errores en refresh-token (error de servidor = 500, 400, etc.)
        const silenciado = handleHttpError(error, alertService, errorReporting);
        if (silenciado) return EMPTY;
        return throwError(() => error);
      }

      // ── Fallo de red en el resto de endpoints ─────────────────────────────
      if (esErrorDeRed(error)) {
        // Consulta pública: pantalla /sin-conexion en lugar de cartel (va antes que REINTENTO).
        if (req.context.get(SIN_CONEXION)) {
          errorRed.irASinConexion();
          marcarErrorManejado(error);
          return throwError(() => error);
        }

        const reintento = req.context.get(REINTENTO);
        const crear     = req.context.get(CREAR);
        const editar    = req.context.get(EDITAR);
        const eliminar  = req.context.get(ELIMINAR);

        if (reintento) {
          // reintentoCtx(false): silencio y sin marcar, así quien hizo la request
          // (getConSelector / mostrarErrorFinal o el componente) muestra su propio aviso.
          if (!reintento.regresarAlInicio) return throwError(() => error);
          errorRed.mostrarErrorConsulta();
        } else if (crear) {
          errorRed.mostrarErrorCrear(crear);
        } else if (editar) {
          errorRed.mostrarErrorEditar(editar);
        } else if (eliminar) {
          errorRed.mostrarErrorEliminar(eliminar);
        } else {
          errorRed.mostrarErrorSinContexto();
        }

        marcarErrorManejado(error);
        return throwError(() => error);
      }

      // ── Altas con errores de negocio propios (factura y nota de crédito) ──
      // Si el body trae internalStatusCS, el cartel lo muestra el componente: evita el doble cartel
      // (por ejemplo "No tenés autorización." ante un 109). Sin internalStatusCS sigue el genérico.
      const crearConfig = req.context.get(CREAR);
      if (crearConfig?.erroresEnComponente && codigoInterno != null) {
        return throwError(() => error);
      }

      // ── Resto de errores → manejo genérico ────────────────────────────────
      const silenciado = handleHttpError(error, alertService, errorReporting);
      if (silenciado) return EMPTY;
      return throwError(() => error);
    })
  );
};